import {Button, Checkbox, Flex, Form, Input, InputNumber, Radio, Upload} from "antd";
import TextArea from "antd/es/input/TextArea";
import {CollectionEditor} from "components/Input/CollectionEditor";
import {EditAuthorDialog} from "routes/Dialogs/EditAuthorDialog";
import {required} from "utilities/formRules";
import {useEffect, useState} from "react";
import {useCreateArticleVersion} from "hooks/useCreateArticleVersion";
import {useAuth} from "hooks/useAuth";
import {useJournal} from "hooks/useJournal";
import {GetArticleForRevisionDocument, UserAction} from "generated/graphql";
import {Navigate, useParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {UserPickElement} from "components/Picker/UserPickElement";
import {useWatch} from "antd/es/form/Form";
import {useRetrieveArxivData} from "hooks/useRetrieveArxivData";

export const Submit = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const createArticleVersion = useCreateArticleVersion();
  const [number, setNumber] = useState<number | null>(null);

  const { number: targetNumber } = useParams();
  const { data, loading: articleLoading } = useQuery(GetArticleForRevisionDocument, {
    variables: { number: +(targetNumber ?? "") },
    skip: !targetNumber
  });
  const article = data?.article;

  const authors = useWatch("authors", form);

  const submit = async () => {
    const { author, file, commentFile, noArxiv, ...values } = await form.validateFields();
    setLoading(true);
    const res = await createArticleVersion({
      articleId: article?.id,
      authorId: author.id,
      file: file.file,
      commentFile: commentFile?.file,
      ...values
    });
    if (!res.data?.createArticleVersion.number) {
      alert("Failed to submit. You can only submit one paper every four months.");
    }
    setNumber(res.data?.createArticleVersion.number ?? null);
    setLoading(false);
  }

  const { user, can } = useAuth();
  const journal = useJournal();

  const aiUsage = Form.useWatch("aiUsage", form) as string;
  const noArxiv = Form.useWatch("noArxiv", form) as boolean;
  const [arxivRetrieved, setArxivRetrieved] = useState(false);
  const enableFields = arxivRetrieved || noArxiv || !journal.featureSettings.useArxivFlow;

  useEffect(() => {
    if (article?.lastVersion.arxivId) setArxivRetrieved(true);
  }, [article?.lastVersion.arxivId]);

  const [retrieveArxivData] = useRetrieveArxivData();
  const getArxivDetails = async () => {
    const arxivId = await form.getFieldValue("arxivId");
    const result = await retrieveArxivData({ variables: { input: { arxivId } } });
    setArxivRetrieved(!!result.data?.retrieveArxivData.title);
    if (!result.data?.retrieveArxivData.title) {
      alert("Could not retrieve data from arXiv. Check the arXiv id and try again.");
    } else {
      form.setFieldValue("title", result.data?.retrieveArxivData.title);
    }
  };

  return <>
    { !number && !articleLoading &&
    <Form form={form} requiredMark={false} layout="vertical" style={{ maxWidth: 800 }}
          initialValues={{
            author: article?.author ?? { id: user?.id, displayName: user?.displayName },
            title: article?.title,
            arxivId: article?.lastVersion.arxivId,
            arxivExplanation: article?.lastVersion.arxivExplanation,
            aiUsage: article?.lastVersion.aiUsage,
            aiUsageExplanation: article?.lastVersion.aiUsageExplanation,
            authors: article?.authors.map(a => ({
              firstName: a.firstName,
              lastName: a.lastName,
              email: a.email,
              affiliation: a.affiliation,
              countryCode: a.countryCode
            }))
    }}>
      { journal.featureSettings.useArxivFlow && <>
        <Form.Item label="arXiv id">
            <Flex gap={10}>
                <Form.Item name="arxivId" style={{ marginBottom: 0 }}>
                  <Input disabled={noArxiv} />
                </Form.Item>
                <Button disabled={noArxiv} onClick={getArxivDetails} type={enableFields ? "default" : "primary"}>Retrieve data</Button></Flex>
        </Form.Item>
        <Form.Item name="noArxiv" valuePropName="checked"  style={{ marginTop: "-15px" }}>
            <label><Checkbox /> My paper is not on arXiv</label>
        </Form.Item>
      </>}
      { noArxiv && <Form.Item name="arxivExplanation" rules={required}
                              label="Please explain why your paper is not on arXiv">
          <TextArea rows={5} />
      </Form.Item> }
      <Form.Item name="author" label="Corresponding author" rules={required}>
        <UserPickElement disabled={!can(UserAction.ManageArticles)} />
      </Form.Item>
      <Form.Item label="Authors" rules={required}>
        <div style={{ fontSize: "small", marginBottom: "5px" }}>Enter all authors (<b>INCLUDING</b> yourself) in the order they appear in the paper.</div>
        <Form.Item name="authors" rules={required}>
          <CollectionEditor format={a => `${a.firstName} ${a.lastName}`}
                            addDialog={EditAuthorDialog}
                            style={{ width: "500px" }}
                            initial={authors?.length ? {} : {
                              firstName: user?.firstName,
                              lastName: user?.lastName,
                              email: user?.mailAddress,
                              affiliation: user?.affiliation
                            }} />
        </Form.Item>
      </Form.Item>
      <Form.Item name="title" label="Title of the paper" rules={required}>
          <Input disabled={!enableFields} />
      </Form.Item>
      <Form.Item name="file" label="File" rules={required}>
        <Upload accept=".pdf" maxCount={1} beforeUpload={_ => false}>
          <Button type="primary" disabled={!enableFields}>Choose file</Button>
        </Upload>
      </Form.Item>
      <Form.Item name="pageCount" label="Number of pages" rules={required}>
        <InputNumber />
      </Form.Item>
      {journal.featureSettings.useArxivFlow && <>
        <Form.Item name="aiUsage" label="Please choose" rules={required}>
            <Radio.Group>
                <Radio value="NONE">No AI was used in the preparation of this manuscript</Radio>
                <Radio value="EXPLAINED">A declaration describing AI use is included in the acknowledgements section of the manuscript</Radio>
            </Radio.Group>
        </Form.Item>
        { aiUsage === "EXPLAINED" && <Form.Item name="aiUsageExplanation" rules={required}
                   label="Paste the contents of the AI use declaration here">
            <TextArea rows={5} />
        </Form.Item> }
      </>}
      <Form.Item name="comment" label="Comment">
        <TextArea rows={10} />
      </Form.Item>
      { article && <Form.Item name="commentFile" label="Additional file">
          <Upload accept=".pdf" maxCount={1} beforeUpload={_ => false}>
              <Button type="primary">Choose file</Button>
          </Upload>
      </Form.Item> }
      <Button size="large" loading={loading} type="primary" disabled={!enableFields} onClick={submit}>Submit</Button>
    </Form> }
    { number && !article && <p>Thank you for submitting. Your manuscript has number {journal.code} {number}.</p> }
    { number && article && <Navigate to=".." /> }
  </>
};